import React, { useCallback, useEffect, useState } from 'react';
import {
    Paper, Typography, Box, Button, TextField, MenuItem, Chip, Checkbox, FormControlLabel, Divider,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    getSignupCatalogService, getSignupApplicationsService, getSignupApplicationDocumentService, reviewSignupApplicationService,
} from '../../../../services/billing.service';
import { toastSuccess } from '../../../../utils/utils';
import { displayCountry, useGeo } from '../../../../utils/geo';

/**
 * SignupApplicationsTab — everything submitted through the portal's sign-up chatbot
 * (/powered-by-dq/chatbot), in ONE list (19 Oct 2026) filtered by type — all types by
 * default. The types, and what DQ does with each:
 *
 *   DQ Pro applicants        approve (emails DP-9.2.2 with the payment link) or reject (DP-9.2.1)
 *   Government verification  approve or reject (the COS Government Assessment)
 *   #DQEveryChild            close once handled
 *   Waitlist                 close once handled
 *   Direct orders            activate the workspace once paid (payment never does);
 *                            mark delivered — solutions DQ fulfils by hand, once paid
 *
 * Each row opens the full submission: contact, organisation, legal entity, the
 * deployment and order, the checklist, documents (downloadable) and the review.
 */

/** The application types, in the order the Type filter lists them. */
const TYPES = [
    { type: 'dq_pro_applicant', label: 'DQ Pro applicants' },
    { type: 'gov_verification', label: 'Government verification' },
    { type: 'dqeverychild', label: '#DQEveryChild' },
    { type: 'waitlist', label: 'Waitlist' },
    { type: 'direct_order', label: 'Direct orders' },
    // The DQ Pro V2 sign-up page opened directly (no chat): no solution, approve = account.
    { type: 'account_request', label: 'Sign-up requests' },
];

const typeLabel = (type) => TYPES.find((t) => t.type === type)?.label || type || '—';

const STATUS = {
    draft:            { label: 'Draft',            color: '#374151', bg: '#F3F4F6' },
    submitted:        { label: 'Submitted',        color: '#1E40AF', bg: '#DBEAFE' },
    awaiting_payment: { label: 'Awaiting payment', color: '#92400E', bg: '#FEF3C7' },
    paid:             { label: 'Paid',             color: '#166534', bg: '#DCFCE7' },
    fulfilled:        { label: 'Fulfilled',        color: '#166534', bg: '#DCFCE7' },
    approved:         { label: 'Approved',         color: '#166534', bg: '#DCFCE7' },
    rejected:         { label: 'Rejected',         color: '#991B1B', bg: '#FEE2E2' },
    closed:           { label: 'Closed',           color: '#374151', bg: '#E5E7EB' },
};

/** Actions per type: [action, button label, the confirmation, needs a note]. */
const ACTIONS = {
    dq_pro_applicant: (a) => {
        if (a.status === 'submitted') {
            return [
                { action: 'approve', label: 'Approve', text: 'Approving emails the applicant DP-9.2.2: eligibility confirmed, with a link to pay the partner plan’s one-time price and, when the plan has one, its first year of solution access.' },
                { action: 'reject', label: 'Reject', danger: true, text: 'Rejecting emails the applicant DP-9.2.1: the direct organizational route is the right fit.' },
            ];
        }
        // Paying never activates a partner's workspace — DQ does, here.
        if (a.awaiting_activation) {
            return [{ action: 'activate', label: 'Activate workspace', text: 'Activates the partner’s login and emails the workspace activation email with a temporary password. Their first year of solution access starts today.' }];
        }
        return [];
    },
    gov_verification: (a) => (a.status === 'submitted' ? [
        { action: 'approve', label: 'Approve', text: 'Records the verification as approved. No email is sent — contact the applicant about access.' },
        { action: 'reject', label: 'Reject', danger: true, text: 'Records the verification as rejected. No email is sent — contact the applicant.' },
    ] : []),
    dqeverychild: (a) => (a.status === 'submitted' ? [{ action: 'close', label: 'Close', text: 'Close this request once the DQ team has handled it.' }] : []),
    waitlist: (a) => (a.status === 'submitted' ? [{ action: 'close', label: 'Close', text: 'Close this waitlist entry once it has been handled.' }] : []),
    direct_order: (a) => [
        // Paying never activates the workspace — DQ does, here.
        ...(a.awaiting_activation ? [{ action: 'activate', label: 'Activate workspace', text: 'Activates the buyer’s login and emails the school or network activation email with a temporary password. Their plan, organization, licences and credits were set up at payment.' }] : []),
        ...(a.manual_fulfilment && a.status === 'paid' ? [{ action: 'fulfil', label: 'Mark delivered', text: 'Marks this paid order as delivered by the DQ team.' }] : []),
    ],
    account_request: (a) => (a.status === 'submitted' ? [
        { action: 'approve', label: 'Approve', text: 'Approving creates the account on the default plan with the password the applicant chose, and emails the activation email. They confirm their email with a code at first sign-in. Assign a plan afterwards as usual.' },
        { action: 'reject', label: 'Reject', danger: true, text: 'Rejecting emails the applicant that their sign-up request was not approved.' },
    ] : []),
};

/** Any rejected application can be put back to Submitted, so the decision can be made again. */
const UNREJECT = { action: 'unreject', label: 'Unreject', text: 'Puts this rejected application back to Submitted so it can be decided again. No email is sent.' };

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const humanKey = (k) => String(k).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const openBase64 = ({ file_name, mime_type, content_base64 }) => {
    const bytes = Uint8Array.from(atob(content_base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: mime_type || 'application/octet-stream' }));
    const win = window.open(url, '_blank', 'noopener');
    if (!win) {
        const a = document.createElement('a');
        a.href = url;
        a.download = file_name || 'document';
        document.body.appendChild(a);
        a.click();
        a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const StatusChip = ({ status }) => {
    const st = STATUS[status] || { label: status || '—', color: '#374151', bg: '#F3F4F6' };
    return <Chip size="small" label={st.label} style={{ background: st.bg, color: st.color, fontWeight: 700 }}/>;
};

/** The label the chatbot showed for a stored value, from the catalog list it came from. */
const labelIn = (list, v) => (Array.isArray(list) ? list.find((o) => o.value === v)?.label : null) || v;
const labelsIn = (list, vs) => (Array.isArray(vs) ? vs.map((v) => labelIn(list, v)).join(', ') : labelIn(list, vs));

const Field = ({ label, children }) => (
    <Box display="flex" style={{ gap: 12, padding: '3px 0' }}>
        <Typography variant="body2" color="textSecondary" style={{ minWidth: 190, flexShrink: 0 }}>{label}</Typography>
        <Typography variant="body2" component="div" style={{ wordBreak: 'break-word' }}>{children === '' || children == null ? '—' : children}</Typography>
    </Box>
);

const Section = ({ title, children }) => (
    <Box mt={2.5}>
        <Typography variant="overline" color="textSecondary">{title}</Typography>
        <Divider style={{ marginBottom: 6 }}/>
        {children}
    </Box>
);

const addressText = (a = {}) => [a.line1, a.line2, a.city, a.state, a.postal_code].filter(Boolean).join(', ');

/** A stored object (deployment, #DQEveryChild answers) as label/value rows. */
const PlainObject = ({ value }) => {
    if (!value || typeof value !== 'object') return null;
    return Object.entries(value).map(([k, v]) => (
        <Field key={k} label={humanKey(k)}>
            {Array.isArray(v)
                ? v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')
                : (v && typeof v === 'object' ? JSON.stringify(v) : String(v))}
        </Field>
    ));
};

const DetailDialog = ({ app, catalog, onClose, onAction }) => {
    useGeo(); // re-render with the API's country list once it loads
    const [docBusy, setDocBusy] = useState(null);
    if (!app) return null;
    const actions = [
        ...(ACTIONS[app.type] || (() => []))(app),
        ...(app.status === 'rejected' ? [UNREJECT] : []),
    ];
    const cl = catalog?.CHECKLIST || {};

    const openDocument = async (index) => {
        setDocBusy(index);
        try {
            const res = await getSignupApplicationDocumentService(app.id, index);
            if (res?.data?.content_base64) openBase64(res.data);
        } catch (err) {
            console.error('[SignupApplicationsTab] document failed:', err);
        } finally {
            setDocBusy(null);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle disableTypography>
                <Typography variant="h6">{app.reference_no} — {app.organization?.name || 'Application'}</Typography>
                <Box mt={0.5} display="flex" alignItems="center" style={{ gap: 8 }}>
                    <StatusChip status={app.status}/>
                    <Typography variant="body2" color="textSecondary">{app.solution_label || 'No solution chosen'} · received {fmtDate(app.submitted_at || app.createdAt)}{app.from_signup_page ? ' · from the sign-up page' : ''}</Typography>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {(app.priority || app.classification_label || app.fulfilment_error) && (
                    <Box mb={1}>
                        {app.classification_label && <Field label="Classification">{app.classification_label}</Field>}
                        {app.priority && <Field label="Priority">{(app.priority_reasons || []).join('; ') || 'Yes'}</Field>}
                        {app.fulfilment_error && <Field label="Fulfilment problem"><span style={{ color: '#b91c1c' }}>{app.fulfilment_error}</span></Field>}
                    </Box>
                )}

                <Section title="Contact">
                    <Field label="Name">{[app.contact?.first_name, app.contact?.last_name].filter(Boolean).join(' ')}</Field>
                    <Field label="Email">{app.contact?.email}</Field>
                    <Field label="Phone">{[app.contact?.phone_country_code, app.contact?.phone_number].filter(Boolean).join(' ')}</Field>
                    <Field label="Job title">{app.contact?.job_title}</Field>
                </Section>

                <Section title="Organization">
                    <Field label="Name">{app.organization?.name}</Field>
                    <Field label="Type">{app.organization?.type === 'other' ? `Other: ${app.organization?.type_other}` : labelIn(catalog?.ORGANIZATION_TYPES, app.organization?.type)}</Field>
                    <Field label="Primary roles">
                        {labelsIn(catalog?.PRIMARY_ROLES, app.organization?.primary_roles)}
                        {app.organization?.primary_roles_other ? ` (${app.organization.primary_roles_other})` : ''}
                    </Field>
                    <Field label="Country">{displayCountry(app.organization?.country)}</Field>
                    <Field label="Address">{addressText(app.organization?.address)}</Field>
                    <Field label="Website">{app.organization?.website}</Field>
                </Section>

                {app.legal_entity?.name && (
                    <Section title="Legal entity">
                        <Field label="Name">{app.legal_entity.name}</Field>
                        <Field label="Registration number">{app.legal_entity.registration_number}</Field>
                        <Field label="Country">{displayCountry(app.legal_entity.country)}</Field>
                        <Field label="Address">{app.legal_entity.same_as_organization_address ? 'Same as organization' : addressText(app.legal_entity.address)}</Field>
                    </Section>
                )}

                {app.deployment && Object.keys(app.deployment).length > 0 && (
                    <Section title={app.type === 'dqeverychild' ? '#DQEveryChild request' : 'Deployment'}>
                        <PlainObject value={app.deployment}/>
                    </Section>
                )}

                {app.invoice && (
                    <Section title="Sign-up invoice">
                        {(app.invoice.lines || []).map((l) => (
                            <Field key={l.key} label={l.description}>
                                {`US$${Number(l.amount_usd).toLocaleString('en-US', { minimumFractionDigits: 2 })}${l.billing === 'annual' ? ' / year' : ''}`}
                            </Field>
                        ))}
                        <Field label={<strong>Total</strong>}><strong>{`US$${Number(app.invoice.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}</strong></Field>
                        {app.plan_key && <Field label="Plan">{app.plan_key}</Field>}
                        {app.order_id && <Field label="Order">{app.order_id}</Field>}
                    </Section>
                )}

                {app.quote && (
                    <Section title="Order">
                        {(app.quote.lines || []).map((l) => (
                            <Field key={l.key} label={l.label}>
                                {l.quoted_separately ? 'Quoted separately' : `US$${Number(l.amount_usd).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                            </Field>
                        ))}
                        <Field label={<strong>Total</strong>}><strong>{app.quote.total_display || `US$${app.quote.total_usd}`}</strong></Field>
                        {app.plan_key && <Field label="Plan">{app.plan_key}</Field>}
                        {app.order_id && <Field label="Order">{app.order_id}</Field>}
                    </Section>
                )}

                {(app.screening?.intended_use || app.checklist) && (
                    <Section title="DQ Pro eligibility">
                        <Field label="Intended use (DP-7)">{labelIn(catalog?.INTENDED_USE, app.screening?.intended_use)}</Field>
                        {app.checklist && Object.entries(app.checklist).map(([k, v]) => (
                            <Field key={k} label={humanKey(k)}>{cl[k] ? labelsIn(cl[k], v) : (Array.isArray(v) ? v.join(', ') : String(v))}</Field>
                        ))}
                    </Section>
                )}

                {(app.documents || []).length > 0 && (
                    <Section title="Documents">
                        {app.documents.map((d) => (
                            <Field key={d.index} label={d.doc_type === 'other' ? `Other: ${d.doc_type_other}` : (labelIn(catalog?.DOCUMENT_TYPES, d.doc_type) || 'Document')}>
                                {d.has_file && (
                                    <Button size="small" color="primary" style={{ textTransform: 'none', padding: 0 }}
                                        onClick={() => openDocument(d.index)} disabled={docBusy === d.index}>
                                        {docBusy === d.index ? <CircularProgress size={14}/> : (d.file_name || 'Open file')}
                                    </Button>
                                )}
                                {d.link && <div><a href={d.link} target="_blank" rel="noopener noreferrer">{d.link}</a></div>}
                                <Typography variant="caption" color="textSecondary">{fmtDate(d.uploaded_at)}</Typography>
                            </Field>
                        ))}
                        {app.documents_note && <Field label="Applicant’s note">{app.documents_note}</Field>}
                    </Section>
                )}

                {(app.review?.reviewed_at || app.workspace?.dq_insider_id) && (
                    <Section title="Review and workspace">
                        {app.review?.reviewed_at && (
                            <>
                                <Field label="Decision">{app.review.decision}</Field>
                                <Field label="Reviewed">{fmtDate(app.review.reviewed_at)}{app.review.reviewed_by?.label ? ` · ${app.review.reviewed_by.label}` : ''}</Field>
                                <Field label="Note">{app.review.note}</Field>
                            </>
                        )}
                        {app.workspace?.dq_insider_id && (
                            <>
                                <Field label="Workspace">{app.workspace.dq_insider_id}</Field>
                                <Field label="Organization code">{app.workspace.org_code}</Field>
                                <Field label="Activated">
                                    {app.awaiting_activation ? 'Paid — waiting for activation' : fmtDate(app.workspace.activated_at)}
                                    {app.workspace.activated_by?.label ? ` · ${app.workspace.activated_by.label}` : ''}
                                </Field>
                            </>
                        )}
                    </Section>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {actions.map((a) => (
                    <Button key={a.action} variant="contained" onClick={() => onAction(app, a)}
                        color={a.danger ? 'default' : 'primary'}
                        style={a.danger ? { background: '#b91c1c', color: '#fff' } : undefined}>
                        {a.label}
                    </Button>
                ))}
            </DialogActions>
        </Dialog>
    );
};

const SignupApplicationsTab = () => {
    useGeo(); // re-render with the API's country list once it loads
    // '' = every type (the default).
    const [type, setType] = useState('');
    const [status, setStatus] = useState('');
    const [priority, setPriority] = useState(false);
    const [search, setSearch] = useState('');
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ applications: [], total: 0 });
    const [catalog, setCatalog] = useState(null);
    const [open, setOpen] = useState(null);
    const [confirm, setConfirm] = useState(null);      // { app, action }
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getSignupCatalogService().then((res) => setCatalog(res?.data || null)).catch(() => setCatalog(null));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getSignupApplicationsService({
                type: type || undefined, status: status || undefined, priority: priority || undefined, search: search || undefined, page: page + 1, limit,
            });
            setData({ applications: res?.data?.applications || [], total: res?.data?.total || 0 });
        } catch (err) {
            console.error('[SignupApplicationsTab] load failed:', err);
        } finally {
            setLoading(false);
        }
    }, [type, status, priority, search, page, limit]);

    useEffect(() => { load(); }, [load]);

    // Search as you stop typing.
    useEffect(() => {
        const t = setTimeout(() => { setSearch(searchText.trim()); setPage(0); }, 400);
        return () => clearTimeout(t);
    }, [searchText]);

    const handleAction = async () => {
        if (!confirm) return;
        setSaving(true);
        try {
            const res = await reviewSignupApplicationService({ id: confirm.app.id, action: confirm.action.action, note: note.trim() || undefined });
            const updated = res?.data?.application;
            toastSuccess(`${confirm.app.reference_no}: ${STATUS[updated?.status]?.label || 'updated'}.`);
            setConfirm(null);
            setNote('');
            setOpen(updated || null);
            await load();
        } catch (err) {
            // invokeApi has already shown the API's reason.
            console.error('[SignupApplicationsTab] action failed:', err);
        } finally {
            setSaving(false);
        }
    };

    const columns = type === 'direct_order' ? 'Order' : type === 'waitlist' ? 'Waitlist for' : 'Solution / order';

    return (
        <Paper style={{ padding: 24 }}>
            <Box mb={1}>
                <Typography variant="h6" style={{ fontWeight: 700 }}>Sign-up applications</Typography>
                <Typography variant="body2" color="textSecondary">
                    Everything submitted through the sign-up chatbot (/powered-by-dq/chatbot). Open a row to see the full submission and act on it.
                </Typography>
            </Box>

            <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 16 }} my={2}>
                <TextField select size="small" variant="outlined" label="Type" value={type}
                    onChange={(e) => { setType(e.target.value); setPriority(false); setPage(0); }} style={{ minWidth: 220 }}>
                    <MenuItem value="">All types</MenuItem>
                    {TYPES.map((t) => <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>)}
                </TextField>
                <TextField size="small" variant="outlined" label="Search reference, email or organization" value={searchText}
                    onChange={(e) => setSearchText(e.target.value)} style={{ minWidth: 300 }}/>
                <TextField select size="small" variant="outlined" label="Status" value={status}
                    onChange={(e) => { setStatus(e.target.value); setPage(0); }} style={{ minWidth: 180 }}>
                    <MenuItem value="">All</MenuItem>
                    {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
                {['', 'dqeverychild', 'waitlist'].includes(type) && (
                    <FormControlLabel
                        control={<Checkbox checked={priority} onChange={(e) => { setPriority(e.target.checked); setPage(0); }} color="primary"/>}
                        label="Priority only"
                    />
                )}
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Received</TableCell>
                            <TableCell>Reference</TableCell>
                            {!type && <TableCell>Type</TableCell>}
                            <TableCell>Organization</TableCell>
                            <TableCell>Contact</TableCell>
                            <TableCell>{columns}</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right"/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={type ? 7 : 8} align="center"><CircularProgress size={24}/></TableCell></TableRow>
                        ) : data.applications.length === 0 ? (
                            <TableRow><TableCell colSpan={type ? 7 : 8} align="center">
                                <Typography variant="body2" color="textSecondary">No applications.</Typography>
                            </TableCell></TableRow>
                        ) : data.applications.map((a) => (
                            <TableRow key={a.id} hover style={{ cursor: 'pointer' }} onClick={() => setOpen(a)}>
                                <TableCell>{fmtDate(a.submitted_at || a.createdAt)}</TableCell>
                                <TableCell>
                                    <Typography variant="body2" style={{ fontWeight: 600 }}>{a.reference_no}</Typography>
                                    {a.priority && <Chip size="small" label="Priority" style={{ background: '#FFF4ED', color: '#C2410C', fontWeight: 700, marginTop: 2 }}/>}
                                </TableCell>
                                {!type && <TableCell><Typography variant="body2">{typeLabel(a.type)}</Typography></TableCell>}
                                <TableCell>
                                    <Typography variant="body2">{a.organization?.name || '—'}</Typography>
                                    <Typography variant="caption" color="textSecondary">{displayCountry(a.organization?.country)}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{[a.contact?.first_name, a.contact?.last_name].filter(Boolean).join(' ') || '—'}</Typography>
                                    <Typography variant="caption" color="textSecondary">{a.contact?.email}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{a.solution_label || '—'}</Typography>
                                    {a.type === 'direct_order' && a.quote?.total_display && (
                                        <Typography variant="caption" color="textSecondary">{a.quote.total_display}{a.manual_fulfilment ? ' · delivered by DQ' : ''}</Typography>
                                    )}
                                    {a.type === 'dq_pro_applicant' && a.classification_label && (
                                        <Typography variant="caption" color="textSecondary">{a.classification_label}</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <StatusChip status={a.status}/>
                                    {a.awaiting_activation && <div><Typography variant="caption" color="textSecondary">Waiting for activation</Typography></div>}
                                </TableCell>
                                <TableCell align="right"><Button size="small" color="primary">View</Button></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div" count={data.total} page={page} rowsPerPage={limit}
                onChangePage={(_, p) => setPage(p)}
                onPageChange={(_, p) => setPage(p)}
                onChangeRowsPerPage={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50]}
            />

            <DetailDialog
                app={open}
                catalog={catalog}
                onClose={() => setOpen(null)}
                onAction={(app, action) => { setNote(''); setConfirm({ app, action }); }}
            />

            <Dialog open={!!confirm} onClose={() => !saving && setConfirm(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{confirm ? `${confirm.action.label} — ${confirm.app.reference_no}` : ''}</DialogTitle>
                <DialogContent>
                    {confirm && (
                        <>
                            <Typography variant="body2" gutterBottom>{confirm.action.text}</Typography>
                            <TextField
                                fullWidth multiline minRows={3} variant="outlined" size="small" style={{ marginTop: 12 }}
                                label="Note (optional) — kept on the application, never emailed"
                                value={note}
                                onChange={(e) => { const { value } = e.target; setNote(value.slice(0, 5000)); }}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={saving}>Cancel</Button>
                    <Button onClick={handleAction} disabled={saving} variant="contained"
                        color={confirm?.action.danger ? 'default' : 'primary'}
                        style={confirm?.action.danger ? { background: '#b91c1c', color: '#fff' } : undefined}>
                        {saving ? <CircularProgress size={18}/> : (confirm?.action.label || 'Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default SignupApplicationsTab;
